// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { OrganisationOutput, AdminPermissionsEnum, PermissionsEnum, OrganisationLinksOutput } from 'thehive-sdk';
import { ADMIN_ORG_ID, DisplayRouteProps, RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { OrganisationLinkManage } from '@Organisms/Organisation/OrganisationLinkManage/OrganisationLinkManage';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useUserPermissions from '@Hooks/useUserPermissions';
import { THLayout } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/Organisation/eventList';
import { THList } from '@Molecules/THList';
import { ICONS } from '@Constants/iconsApp';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector, computeListSelector } from '@Organisms/Organisation/selector';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Space } from 'antd';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import useListQuery from '@Hooks/useListQuery';
import { useGenericQuery } from '@Queries/genericQueries';
import usePageTitle from '@Hooks/usePageTitle';

import './LinkedOrgList.scss';

const LinkedOrgList = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();

    const { data: currentOrganisation, refetch: refetchCurrentOrganisation } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId }),
        manual: true,
        fetchOnStart: true,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: false,
    });

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_ORGANISATION] });

    const columns = columnsRenderer<OrganisationLinksOutput>([
        {
            className: 'links-list__column-name',
            render: ({ toOrganisation }) => {
                const org = orgList?.find(({ name }) => name === toOrganisation);

                return (
                    <Space>
                        <UserAvatar name={org?.name} avatar={org?.avatar} size="large" />
                        {toOrganisation}
                    </Space>
                );
            },
        },
        {
            className: 'links-list__column-link-type',
            render: ({ linkType, toOrganisation }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === linkType);

                const Content = (
                    <ColorLabel
                        color="primary-color"
                        label={
                            <div className="organisation-drawer__links__item">
                                {currentOrganisation?.name || ''}
                                <ICONS.ArrowRightOutlined />
                                {toOrganisation} ({linkType})
                            </div>
                        }
                    />
                );

                return (
                    <THTooltip
                        title={
                            sharingProfile?.autoShare
                                ? i('organisation.links.form.link-type.help.shared', {
                                      currentOrganisation: currentOrganisation?.name || '',
                                      otherOrganisation: toOrganisation || '',
                                      profile: sharingProfile?.permissionProfile,
                                  })
                                : i('organisation.links.form.link-type.help.not-shared', {
                                      currentOrganisation: currentOrganisation?.name || '',
                                      otherOrganisation: toOrganisation || '',
                                  })
                        }
                    >
                        {Content}
                    </THTooltip>
                );
            },
        },
        {
            className: 'links-list__column-other-link-type',
            render: ({ otherLinkType, toOrganisation }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === otherLinkType);

                const Content = (
                    <ColorLabel
                        color="primary-color"
                        label={
                            <div className="organisation-drawer__links__item">
                                {currentOrganisation?.name || ''}
                                <ICONS.ArrowLeftOutlined />
                                {toOrganisation} ({otherLinkType})
                            </div>
                        }
                    />
                );

                return (
                    <THTooltip
                        title={
                            sharingProfile?.autoShare
                                ? i('organisation.links.form.other-link-type.help.shared', {
                                      currentOrganisation: currentOrganisation?.name || '',
                                      otherOrganisation: toOrganisation || '',
                                      profile: sharingProfile?.permissionProfile,
                                  })
                                : i('organisation.links.form.other-link-type.help.not-shared', {
                                      currentOrganisation: currentOrganisation?.name || '',
                                      otherOrganisation: toOrganisation || '',
                                  })
                        }
                    >
                        {Content}
                    </THTooltip>
                );
            },
        },
    ]);
    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.OrganisationController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    refetchCurrentOrganisation();
                },
            }),
        [i, refetchCurrentOrganisation],
    );

    usePageTitle(i('page-title.organisation.linked-organisations', { companyName: orgId }));

    return (
        <THLayout>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <OrganisationLinkManage
                                triggerElem={<THButton>{i('organisation.links.manage')}</THButton>}
                                onListLinkableOrgs={eventList['onListLinkableOrganisations']}
                                onListSharingProfiles={eventList['onListSharingProfiles']}
                                onSetLinks={eventList['onSetLinks']}
                                orgId={orgId}
                                currentOrganisation={currentOrganisation}
                            />
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    className="linked-org-list"
                    dataSource={(currentOrganisation?.links || []).sort(
                        ({ toOrganisation: orgA = '' }, { toOrganisation: orgB = '' }) => orgA.localeCompare(orgB),
                    )}
                    columns={columns}
                    rowKey="toOrganisation"
                    size="small"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('organisation.links.none')}>
                            {hasPermissions && (
                                <OrganisationLinkManage
                                    triggerElem={<THButton type="link">{i('organisation.links.manage')}</THButton>}
                                    onListLinkableOrgs={eventList['onListLinkableOrganisations']}
                                    onListSharingProfiles={eventList['onListSharingProfiles']}
                                    onSetLinks={eventList['onSetLinks']}
                                    orgId={orgId}
                                    currentOrganisation={currentOrganisation}
                                />
                            )}
                        </DisplayEmptyData>
                    }
                />
            </THList>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.links');
const pathUser = RouteBuilder.compute('organisation.links');

const checkOrgId = ({ params, isAdmin }: DisplayRouteProps): boolean => {
    if (params?.orgId && params?.orgId === ADMIN_ORG_ID && isAdmin) {
        return false;
    }
    return true;
};

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.links',
        icon: <ICONS.LinkOutlined />,
    },

    path,
    content: <LinkedOrgList />,
    shared: true,
    space: ['admin'],
    permissionNeeded: [PermissionsEnum.MANAGE_ORGANISATION], //org

    displayRoute: checkOrgId,

    breadcrumb: { label: 'breadcrumb.organisation.links' },
};
export const RouteUser: RouteType = {
    ...Route,
    path: pathUser,
    space: ['user'],
};
